import { useMemo } from 'react';
import type { UsageDailyBucket } from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';

/**
 * Stacked daily token bars (input under, output over) — Field Notes
 * rebuild (epic #950 PR-8, formerly the console-shared
 * `createDailyUsageChart` factory instantiation). Bar testids and
 * tooltip/aria strings are part of the test contract.
 */
interface Props {
  readonly buckets: readonly UsageDailyBucket[];
  readonly heightPx?: number;
  readonly testId?: string;
}

const DEFAULT_HEIGHT_PX = 200;
const DEFAULT_WIDTH_PX = 960;
const BAR_GAP_PX = 4;
const PADDING_X_PX = 24;
const PADDING_Y_PX = 24;

export function DailyUsageChart({
  buckets,
  heightPx = DEFAULT_HEIGHT_PX,
  testId,
}: Props): JSX.Element {
  const t = useT();
  const maxTotal = useMemo(() => {
    let top = 0;
    for (const d of buckets) {
      const total = d.inputTokens + d.outputTokens;
      if (total > top) {
        top = total;
      }
    }
    return top;
  }, [buckets]);

  const totalInput = useMemo(() => buckets.reduce((sum, d) => sum + d.inputTokens, 0), [buckets]);
  const totalOutput = useMemo(() => buckets.reduce((sum, d) => sum + d.outputTokens, 0), [buckets]);

  if (buckets.length === 0) {
    return (
      <div
        data-testid={testId}
        className="flex items-center justify-center rounded-lg bg-raised py-16 text-body text-ink-low"
      >
        {t('usage_chart.empty')}
      </div>
    );
  }

  const chartHeight = heightPx;
  const chartWidth = DEFAULT_WIDTH_PX;
  const drawableHeight = chartHeight - PADDING_Y_PX * 2;
  const drawableWidth = chartWidth - PADDING_X_PX * 2;
  const barWidth = Math.max(2, (drawableWidth - BAR_GAP_PX * (buckets.length - 1)) / buckets.length);

  const ariaLabel = t('usage_chart.daily.aria', {
    days: String(buckets.length),
    total: String(totalInput + totalOutput),
    input: String(totalInput),
    output: String(totalOutput),
  });

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
      preserveAspectRatio="none"
      className="block h-48 w-full rounded-lg bg-raised"
    >
      {buckets.map((bucket, index) => {
        const total = bucket.inputTokens + bucket.outputTokens;
        const inputFraction = maxTotal === 0 ? 0 : bucket.inputTokens / maxTotal;
        const outputFraction = maxTotal === 0 ? 0 : bucket.outputTokens / maxTotal;
        const inputHeight = Math.round(drawableHeight * inputFraction);
        const outputHeight = Math.round(drawableHeight * outputFraction);
        const x = PADDING_X_PX + index * (barWidth + BAR_GAP_PX);
        const inputY = PADDING_Y_PX + drawableHeight - inputHeight;
        const outputY = inputY - outputHeight;
        return (
          <g key={bucket.date} data-testid={`usage-daily-bar-${index}`} data-date={bucket.date}>
            <title>
              {t('usage_chart.daily.tooltip', {
                date: bucket.date,
                total: String(total),
                input: String(bucket.inputTokens),
                output: String(bucket.outputTokens),
                requests: String(bucket.requests),
              })}
            </title>
            {inputHeight > 0 ? (
              <rect
                data-testid={`usage-daily-bar-${index}-input`}
                x={x}
                y={inputY}
                width={barWidth}
                height={inputHeight}
                rx={1}
                className="fill-forest"
              />
            ) : null}
            {outputHeight > 0 ? (
              <rect
                data-testid={`usage-daily-bar-${index}-output`}
                x={x}
                y={outputY}
                width={barWidth}
                height={outputHeight}
                rx={1}
                className="fill-rust"
              />
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}
